/**
 * Rental lease tenant subdocs — parse, normalize, display, CSV round-trip.
 * Keep in sync with server/utils/rentalLeaseTenants.js.
 */

import { validateOptionalEmail, validateOptionalPhone } from "./validation";

export const RENTAL_LEASE_TENANTS_MAX = 20;

/**
 * @param {unknown} t
 * @returns {{ full_name: string, email: string, phone: string }}
 */
export function normalizeTenant(t) {
  const full_name = (t?.full_name != null ? String(t.full_name) : "").trim();
  const emailRaw = (t?.email != null ? String(t.email) : "").trim();
  const phone = (t?.phone != null ? String(t.phone) : "").trim();
  return {
    full_name,
    email: emailRaw ? emailRaw.toUpperCase() : "",
    phone,
  };
}

/**
 * Quick-add line: "Name, email, phone" (email and phone optional).
 * @param {unknown} line
 * @returns {{ ok: true, tenant: { full_name: string, email: string, phone: string } } | { ok: false, error: string }}
 */
export function parseTenantQuickAddLine(line) {
  const raw = line != null ? String(line).trim() : "";
  if (!raw) return { ok: false, error: "Enter name, email, phone (comma-separated)" };

  const parts = raw.split(",").map((p) => p.trim());
  const full_name = parts[0] || "";
  if (!full_name) return { ok: false, error: "Tenant name is required" };

  let email = parts[1] || "";
  let phone = parts.length > 3 ? parts.slice(2).join(", ").trim() : parts[2] || "";

  // If middle token looks like a phone and third is empty, treat middle as phone.
  if (email && !phone && !email.includes("@") && /\d/.test(email)) {
    phone = email;
    email = "";
  }

  if (email) {
    const emailCheck = validateOptionalEmail(email);
    if (!emailCheck.valid) return { ok: false, error: emailCheck.message || "Invalid email" };
    email = email.toUpperCase();
  }
  if (phone) {
    const phoneCheck = validateOptionalPhone(phone);
    if (!phoneCheck.valid) return { ok: false, error: phoneCheck.message || "Invalid phone" };
  }

  return { ok: true, tenant: { full_name, email, phone } };
}

/**
 * @param {{ full_name?: string, email?: string, phone?: string }|null|undefined} t
 * @returns {string}
 */
function formatTenantDisplay(t) {
  const n = normalizeTenant(t);
  if (!n.full_name) return "—";
  const bits = [];
  if (n.email) bits.push(n.email);
  if (n.phone) bits.push(n.phone);
  if (bits.length === 0) return n.full_name;
  return `${n.full_name} (${bits.join(" | ")})`;
}

/**
 * List/table summary: names only (no email/phone).
 * @param {unknown} tenants
 * @returns {string}
 */
export function formatTenantNamesDisplay(tenants) {
  const list = normalizeTenantsList(tenants);
  if (list.length === 0) return "—";
  return list.map((t) => t.full_name).filter(Boolean).join(", ") || "—";
}

/**
 * Full contact display for CSV / detail exports.
 * @param {unknown} tenants
 * @returns {string}
 */
export function formatTenantsDisplay(tenants) {
  const list = normalizeTenantsList(tenants);
  if (list.length === 0) return "—";
  return list.map(formatTenantDisplay).join(", ");
}

/**
 * Accept array subdocs or legacy free-text and return normalized tenant objects.
 * @param {unknown} tenants
 * @returns {Array<{ full_name: string, email: string, phone: string }>}
 */
export function normalizeTenantsList(tenants) {
  if (Array.isArray(tenants)) {
    return tenants.map(normalizeTenant).filter((t) => t.full_name);
  }
  if (typeof tenants === "string" && tenants.trim()) {
    return parseTenantsDisplayText(tenants);
  }
  return [];
}

/**
 * Parse display/CSV text like "Name (EMAIL | phone), Name2 (EMAIL2 | phone2)".
 * Also accepts ";" separated quick-add lines.
 * @param {unknown} text
 * @returns {Array<{ full_name: string, email: string, phone: string }>}
 */
export function parseTenantsDisplayText(text) {
  const raw = text != null ? String(text).trim() : "";
  if (!raw) return [];

  if (raw.includes(";") && !raw.includes("(")) {
    return raw
      .split(";")
      .map((line) => parseTenantQuickAddLine(line))
      .filter((r) => r.ok)
      .map((r) => r.tenant);
  }

  // Split on "), " between tenants while keeping closing paren on each chunk.
  const chunks = [];
  let buf = "";
  let depth = 0;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === "(") depth += 1;
    if (ch === ")") depth -= 1;
    if (ch === "," && depth === 0) {
      if (buf.trim()) chunks.push(buf.trim());
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) chunks.push(buf.trim());

  const out = [];
  for (const chunk of chunks) {
    const m = chunk.match(/^(.+?)(?:\s*\((.*)\))?$/);
    if (!m) continue;
    const full_name = (m[1] || "").trim();
    if (!full_name) continue;
    const inside = (m[2] || "").trim();
    let email = "";
    let phone = "";
    if (inside) {
      const pipe = inside.split("|").map((s) => s.trim());
      if (pipe.length >= 2) {
        email = pipe[0];
        phone = pipe.slice(1).join(" | ").trim();
      } else if (inside.includes("@")) {
        email = inside;
      } else {
        phone = inside;
      }
    }
    out.push(normalizeTenant({ full_name, email, phone }));
  }
  return out;
}

/**
 * @param {unknown} tenants
 * @returns {{ ok: true, tenants: Array<{ full_name: string, email: string, phone: string }> } | { ok: false, error: string }}
 */
export function validateTenantsList(tenants) {
  const list = normalizeTenantsList(tenants);
  if (list.length === 0) return { ok: false, error: "At least one tenant is required" };
  if (list.length > RENTAL_LEASE_TENANTS_MAX) {
    return { ok: false, error: `Too many tenants (max ${RENTAL_LEASE_TENANTS_MAX})` };
  }
  for (let i = 0; i < list.length; i++) {
    const t = list[i];
    if (!t.full_name) return { ok: false, error: `Tenant ${i + 1}: Full name is required` };
    const emailCheck = validateOptionalEmail(t.email);
    if (!emailCheck.valid) return { ok: false, error: `Tenant ${i + 1}: ${emailCheck.message}` };
    const phoneCheck = validateOptionalPhone(t.phone);
    if (!phoneCheck.valid) return { ok: false, error: `Tenant ${i + 1}: ${phoneCheck.message}` };
  }
  return { ok: true, tenants: list.map(normalizeTenant) };
}
